import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export type ButtonVariant = "primary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: "gap-1.5 px-3 py-[5px] text-xs",
  md: "gap-2 px-4 py-[7px] text-[13px]",
};

const variantClasses: Record<ButtonVariant, string> = {
  primary: cn(
    "text-white font-medium rounded-md transition-opacity hover:opacity-90"
  ),
  ghost: cn(
    "rounded-md border border-[#e4e8ef] bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
  ),
  danger: cn(
    "rounded-md border border-red-200 bg-white text-red-600 transition-colors hover:bg-red-50"
  ),
};

function Spinner() {
  return (
    <svg
      className="h-[1em] w-[1em] shrink-0 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    icon,
    className,
    disabled,
    children,
    type = "button",
    style,
    ...rest
  },
  ref
) {
  const isDisabled = Boolean(disabled || loading);
  const primaryStyle =
    variant === "primary"
      ? { ...style, background: "linear-gradient(135deg, #1a2744 0%, #b03a6e 100%)" }
      : style;

  const showIconSlot = loading || icon;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      style={primaryStyle}
      className={cn(
        "inline-flex items-center justify-center font-sans",
        sizeClasses[size],
        variantClasses[variant],
        loading && "cursor-wait opacity-70",
        disabled && !loading && "pointer-events-none cursor-not-allowed opacity-50",
        className
      )}
      {...rest}
    >
      {showIconSlot ? (loading ? <Spinner /> : icon) : null}
      {children}
    </button>
  );
});

export default Button;
